<template>
  <div>
    <b-card no-body>
      <b-card-header>
        <b-card-title>مرفقات المعاملات</b-card-title>
        <b-button
          variant="primary"
          @click="openCreateModal"
        >
          <feather-icon
            icon="PlusIcon"
            class="mr-50"
          />
          <span class="align-middle">إضافة نوع مرفق</span>
        </b-button>
      </b-card-header>

      <b-card-body>
        <b-row>
          <b-col
            cols="12"
            md="6"
          >
            <b-form-group
              :label="$t('common.search')"
              label-for="attachment-lookups-search"
            >
              <b-form-input
                id="attachment-lookups-search"
                v-model="filters.search"
                placeholder="ابحث باسم المرفق أو المفتاح"
                @input="debouncedSearch"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group
              :label="$t('common.status')"
              label-for="attachment-lookups-status"
            >
              <v-select
                v-model="filters.is_active"
                :options="statusOptions"
                :reduce="option => option.value"
                label="label"
                :placeholder="$t('common.all')"
                @input="fetchData"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group
              label="عدد العناصر"
              label-for="attachment-lookups-per-page"
            >
              <v-select
                v-model="filters.per_page"
                :options="perPageOptions"
                :reduce="option => option.value"
                label="label"
                @input="handlePerPageChange"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <b-card
      no-body
      class="mt-2"
    >
      <b-table
        :items="attachmentLookups"
        :fields="tableColumns"
        :busy="load"
        responsive
        show-empty
        empty-text="لا توجد بيانات"
        class="position-relative"
      >
        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle mr-1" />
            <strong>{{ $t('common.loading') }}</strong>
          </div>
        </template>

        <template #cell(is_active)="data">
          <b-badge
            :variant="data.item.is_active ? 'success' : 'danger'"
            pill
          >
            {{ data.item.is_active ? $t('common.active') : $t('common.inactive') }}
          </b-badge>
        </template>

        <template #cell(sort_order)="data">
          <span>{{ data.item.sort_order !== null && data.item.sort_order !== undefined ? data.item.sort_order : '-' }}</span>
        </template>

        <template #cell(extensions)="data">
          <div
            v-if="(data.item.extensions || []).length"
            class="d-flex flex-wrap"
          >
            <b-badge
              v-for="ext in data.item.extensions"
              :key="`${data.item.id}-${ext}`"
              variant="light-secondary"
              class="mr-25 mb-25"
            >
              {{ ext }}
            </b-badge>
          </div>
          <span v-else>-</span>
        </template>

        <template #cell(is_multiple)="data">
          <b-badge
            :variant="data.item.is_multiple ? 'info' : 'secondary'"
            pill
          >
            {{ data.item.is_multiple ? 'متعدد' : 'ملف واحد' }}
          </b-badge>
        </template>

        <template #cell(actions)="data">
          <div class="text-nowrap">
            <b-button
              variant="flat-info"
              size="sm"
              @click="openEditModal(data.item)"
            >
              <feather-icon icon="EditIcon" />
            </b-button>
            <b-button
              variant="flat-danger"
              size="sm"
              @click="confirmDelete(data.item)"
            >
              <feather-icon icon="TrashIcon" />
            </b-button>
          </div>
        </template>
      </b-table>

      <b-card-body class="d-flex justify-content-between align-items-center pt-0">
        <div>
          <span class="text-muted">{{ $t('common.showing') }} {{ meta.from || 0 }} {{ $t('common.to') }} {{ meta.to || 0 }} {{ $t('common.of') }} {{ meta.total || 0 }} {{ $t('common.entries') }}</span>
        </div>
        <b-pagination
          v-model="currentPage"
          :total-rows="meta.total || 0"
          :per-page="filters.per_page"
          first-number
          last-number
          prev-class="prev-item"
          next-class="next-item"
          @change="handlePageChange"
        >
          <template #prev-text>
            <feather-icon
              icon="ChevronLeftIcon"
              size="18"
            />
          </template>
          <template #next-text>
            <feather-icon
              icon="ChevronRightIcon"
              size="18"
            />
          </template>
        </b-pagination>
      </b-card-body>
    </b-card>

    <b-modal
      v-model="showFormModal"
      :title="isEdit ? 'تعديل نوع مرفق' : 'إضافة نوع مرفق'"
      :ok-title="isEdit ? $t('common.update') : $t('common.create')"
      :cancel-title="$t('common.cancel')"
      :ok-disabled="submitting"
      @ok="submitForm"
      @hidden="resetForm"
    >
      <b-form @submit.stop.prevent="submitForm">
        <b-form-group
          label="المفتاح *"
          label-for="key"
        >
          <b-form-input
            id="key"
            v-model.trim="form.key"
            :state="errors.key ? false : null"
          />
          <b-form-invalid-feedback v-if="errors.key">
            {{ errors.key[0] }}
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group
          label="الاسم *"
          label-for="name"
        >
          <b-form-input
            id="name"
            v-model.trim="form.name"
            :state="errors.name ? false : null"
          />
          <b-form-invalid-feedback v-if="errors.name">
            {{ errors.name[0] }}
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group
          label="الاسم بالعربية"
          label-for="name_ar"
        >
          <b-form-input
            id="name_ar"
            v-model.trim="form.name_ar"
            dir="rtl"
            :state="errors.name_ar ? false : null"
          />
          <b-form-invalid-feedback v-if="errors.name_ar">
            {{ errors.name_ar[0] }}
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group
          label="الترتيب"
          label-for="sort_order"
        >
          <b-form-input
            id="sort_order"
            v-model.number="form.sort_order"
            type="number"
            min="0"
            :state="errors.sort_order ? false : null"
          />
          <b-form-invalid-feedback v-if="errors.sort_order">
            {{ errors.sort_order[0] }}
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group
          label="الأنواع المسموحة *"
          label-for="extensions"
        >
          <v-select
            id="extensions"
            v-model="form.extensions"
            :options="extensionPresetOptions"
            multiple
            taggable
            :create-option="createExtensionOption"
            placeholder="اختر أو أضف الامتدادات مثل jpg, pdf"
            :class="{ 'is-invalid': errors.extensions }"
          />
          <small class="text-muted d-block mt-25">بدون نقطة. يمكن اختيار أكثر من نوع.</small>
          <b-form-invalid-feedback
            v-if="errors.extensions"
            :state="false"
            class="d-block"
          >
            {{ errors.extensions[0] }}
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group
          label="عدد الملفات"
          label-for="is_multiple"
        >
          <b-form-radio-group
            id="is_multiple"
            v-model="form.is_multiple"
            :options="fileCountOptions"
            buttons
            button-variant="outline-primary"
            :state="errors.is_multiple ? false : null"
          />
          <b-form-invalid-feedback v-if="errors.is_multiple">
            {{ errors.is_multiple[0] }}
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-checkbox
          v-model="form.is_active"
          switch
        >
          {{ $t('common.active') }}
        </b-form-checkbox>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BCardBody,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BButton,
  BTable,
  BBadge,
  BSpinner,
  BPagination,
  BModal,
  BForm,
  BFormCheckbox,
  BFormInvalidFeedback,
  BFormRadioGroup,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { mapState, mapActions } from 'vuex'
import { debounce } from '@/utils/helper'
import {
  ATTACHMENT_EXTENSION_PRESETS,
  parseExtensions,
} from '@/utils/attachmentLookup'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'AttachmentLookupsManagement',
  components: {
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BButton,
    BTable,
    BBadge,
    BSpinner,
    BPagination,
    BModal,
    BForm,
    BFormCheckbox,
    BFormInvalidFeedback,
    BFormRadioGroup,
    vSelect,
  },
  data() {
    return {
      filters: {
        search: '',
        is_active: null,
        per_page: 15,
      },
      currentPage: 1,
      tableColumns: [
        { key: 'id', label: 'ID' },
        { key: 'key', label: 'Key' },
        { key: 'name', label: 'Name' },
        { key: 'name_ar', label: 'الاسم بالعربية' },
        { key: 'sort_order', label: 'الترتيب' },
        { key: 'extensions', label: 'الأنواع' },
        { key: 'is_multiple', label: 'عدد الملفات' },
        { key: 'is_active', label: this.$t('common.status') },
        { key: 'actions', label: this.$t('common.actions') },
      ],
      statusOptions: [
        { label: this.$t('common.all'), value: null },
        { label: this.$t('common.active'), value: true },
        { label: this.$t('common.inactive'), value: false },
      ],
      perPageOptions: [
        { label: '15', value: 15 },
        { label: '25', value: 25 },
        { label: '50', value: 50 },
        { label: '100', value: 100 },
      ],
      showFormModal: false,
      isEdit: false,
      currentId: null,
      submitting: false,
      form: {
        key: '',
        name: '',
        name_ar: '',
        sort_order: 1,
        extensions: [],
        is_multiple: false,
        is_active: true,
      },
      extensionPresetOptions: ATTACHMENT_EXTENSION_PRESETS,
      fileCountOptions: [
        { text: 'ملف واحد', value: false },
        { text: 'متعدد', value: true },
      ],
      errors: {},
    }
  },
  computed: {
    ...mapState('attachmentLookups', ['attachmentLookups', 'meta', 'load']),
  },
  created() {
    this.fetchData()
    this.debouncedSearch = debounce(this.fetchData, 500)
  },
  methods: {
    ...mapActions('attachmentLookups', ['list', 'get', 'create', 'update', 'delete']),
    async fetchData() {
      const params = {
        page: this.currentPage,
        per_page: this.filters.per_page,
      }

      if (this.filters.search) params.search = this.filters.search
      if (this.filters.is_active !== null) params.is_active = this.filters.is_active

      try {
        await this.list(params)
      } catch (error) {
        this.showToast('error', error?.response?.data?.message || 'تعذر تحميل البيانات')
      }
    },
    handlePageChange(page) {
      this.currentPage = page
      this.fetchData()
    },
    handlePerPageChange() {
      this.currentPage = 1
      this.fetchData()
    },
    openCreateModal() {
      this.resetForm()
      this.isEdit = false
      this.currentId = null
      this.showFormModal = true
    },
    async openEditModal(item) {
      this.resetForm()
      this.isEdit = true
      this.currentId = item.id
      this.showFormModal = true
      try {
        const response = await this.get(item.id)
        const data = response?.data || response || item
        this.form = {
          key: data.key || '',
          name: data.name || '',
          name_ar: data.name_ar || '',
          sort_order: Number.isInteger(data.sort_order) ? data.sort_order : 1,
          extensions: parseExtensions(data.extensions),
          is_multiple: data.is_multiple === true || data.is_multiple === 1 || data.is_multiple === '1',
          is_active: !!data.is_active,
        }
      } catch (error) {
        this.showToast('error', error?.response?.data?.message || 'تعذر تحميل العنصر')
      }
    },
    createExtensionOption(value) {
      return String(value || '').trim().toLowerCase().replace(/^\.+/, '')
    },
    buildPayload() {
      return {
        key: this.form.key,
        name: this.form.name,
        name_ar: this.form.name_ar || null,
        sort_order: this.form.sort_order ?? 1,
        extensions: parseExtensions(this.form.extensions),
        is_multiple: !!this.form.is_multiple,
        is_active: this.form.is_active,
      }
    },
    async submitForm(event) {
      if (event) {
        event.preventDefault()
      }

      this.submitting = true
      this.errors = {}

      try {
        const payload = this.buildPayload()
        if (!payload.extensions.length) {
          this.errors = { extensions: ['يجب اختيار نوع ملف واحد على الأقل'] }
          this.showToast('error', this.errors.extensions[0])
          return
        }
        if (this.isEdit && this.currentId) {
          await this.update({ id: this.currentId, data: payload })
          this.showToast('success', 'تم تحديث نوع المرفق بنجاح')
        } else {
          await this.create(payload)
          this.showToast('success', 'تم إنشاء نوع المرفق بنجاح')
        }
        this.showFormModal = false
        await this.fetchData()
      } catch (error) {
        if (error?.response?.status === 422) {
          this.errors = error?.response?.data?.errors || {}
        }
        this.showToast('error', error?.response?.data?.message || 'تعذر حفظ البيانات')
      } finally {
        this.submitting = false
      }
    },
    confirmDelete(item) {
      this.$swal({
        title: 'تأكيد الحذف',
        text: 'هل أنت متأكد من حذف نوع المرفق؟',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('common.yes'),
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      }).then(async result => {
        if (!result.isConfirmed) return
        try {
          await this.delete(item.id)
          this.showToast('success', 'تم الحذف بنجاح')
          await this.fetchData()
        } catch (error) {
          const message = error?.response?.data?.message || 'تعذر حذف العنصر'
          if (this.isLinkedToServiceTypesError(error)) {
            this.showToast('warning', 'لا يمكن حذف هذا النوع لأنه مرتبط بمعاملة واحدة أو أكثر')
            return
          }
          this.showToast('error', message)
        }
      })
    },
    isLinkedToServiceTypesError(error) {
      const message = (error?.response?.data?.message || '').toLowerCase()
      return message.includes('service type') || message.includes('linked') || message.includes('مرتبط')
    },
    resetForm() {
      this.errors = {}
      this.form = {
        key: '',
        name: '',
        name_ar: '',
        sort_order: 1,
        extensions: [],
        is_multiple: false,
        is_active: true,
      }
    },
    showToast(type, text) {
      const map = {
        success: { title: this.$t('common.success'), icon: 'CheckIcon', variant: 'success' },
        warning: { title: this.$t('common.error'), icon: 'AlertTriangleIcon', variant: 'warning' },
        error: { title: this.$t('common.error'), icon: 'AlertTriangleIcon', variant: 'danger' },
      }
      const config = map[type] || map.error
      this.$toast({
        component: ToastificationContent,
        props: {
          ...config,
          text,
        },
      })
    },
  },
}
</script>
